'use client';

import { useQueryClient } from '@tanstack/react-query';
import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';

import { addSeenTour, getSeenTours } from '@/components/shared/components/auto-tour/helper';
import { useGetTour } from '@/components/shared/components/auto-tour/services/hooks';
import { TourData, TransformedTourStep } from '@/components/shared/components/auto-tour/type';
import { useTour } from '@/components/shared/helpers/hooks/useTour';

import { useTourStore } from '@/store/tour.store';

import { GET_TOUR } from '@/constant/query-key';

// Default delay if not provided by backend
const DEFAULT_DELAY = 800;

function AutoTourContent() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();
  const [steps, setSteps] = useState<TransformedTourStep[]>([]);
  const { currentPathname, setShowTour } = useTourStore();
  const [isAfterLogin, setIsAfterLogin] = useState(false);
  const [tourCompleted, setTourCompleted] = useState(false);

  // All hooks must be called before any conditional returns
  const shouldResetTour = searchParams.get('resetTour') === 'true';
  const [hasResetTour, setHasResetTour] = useState(false);
  const [hasSeenTourState, setHasSeenTourState] = useState<boolean | null>(null);

  const param = isAfterLogin ? '/after-login' : pathname;
  const previewId = searchParams.get('preview_id');
  const { refetch, data } = useGetTour(param, previewId);

  useEffect(() => {
    if (pathname === currentPathname && currentPathname !== null) {
      setIsAfterLogin(true);
    } else {
      setIsAfterLogin(false);
    }
  }, [pathname, currentPathname]);

  // Reset tour completed state and steps when pathname or param changes
  useEffect(() => {
    setTourCompleted(false);
    setSteps([]); // Clear old steps when navigating to new page
    setShowTour(false);
  }, [param]);

  useEffect(() => {
    refetch();
  }, [param]);

  // Extract tour data (or use defaults if not available)
  const tourData = data as TourData | undefined;
  const allSteps = tourData?.tour || [];
  const tourKey = tourData?.position || '';
  const tourDelay = tourData?.delay ?? DEFAULT_DELAY;
  const hasSeenTour =
    !previewId && tourData?.id && getSeenTours().includes(String(tourData.id));
  // Initialize hasSeenTour state and handle reset
  useEffect(() => {
    if (typeof window === 'undefined' || !tourKey || allSteps.length === 0) return;

    if (shouldResetTour && !hasResetTour) {
      setHasResetTour(true);
      setHasSeenTourState(false);
      setTourCompleted(false);
      // Invalidate tour query to refetch from backend
      queryClient.invalidateQueries({ queryKey: [GET_TOUR, param, previewId] });
      // Remove the query parameter from URL
      const url = new URL(window.location.href);
      url.searchParams.delete('resetTour');
      window.history.replaceState({}, '', url.toString());
    }
  }, [shouldResetTour, tourKey, hasResetTour, hasSeenTourState, allSteps.length]);

  // Clear steps if no tour data is available
  useEffect(() => {
    if (allSteps.length === 0) {
      setSteps([]);
      setShowTour(false);
    }
  }, [allSteps.length]);

  // Wait for elements to be rendered before checking visibility
  useEffect(() => {
    if (typeof window === 'undefined' || !tourKey || allSteps.length === 0) return;

    let retryCount = 0;
    const maxRetries = 30; // 3 seconds max (30 * 100ms)

    // Check if elements exist and are visible
    const checkElements = () => {
      // Check visibility - don't activate tabs, just verify elements or their tabs exist
      const availableSteps = allSteps.filter((step) => {
        // Add '#' prefix if not present
        const elementSelector =
          step.element.startsWith('#') || step.element.startsWith('.')
            ? step.element
            : `#${step.element}`;

        const element = document.querySelector(elementSelector);

        // If step has a tabId, check if the tab exists instead of the element
        if (step.tabId && step.tabId !== 'productDescription') {
          const tabSelector =
            step.tabId.startsWith('#') || step.tabId.startsWith('.')
              ? step.tabId
              : `#${step.tabId}`;
          const tabElement = document.querySelector(tabSelector);

          if (tabElement) {
            // Tab exists, so we trust the element will exist when tab is activated
            return true;
          } else {
            // Tab doesn't exist, this is conditional content
            return false;
          }
        }

        // For elements without tabId, check if currently visible
        if (!element) {
          return false;
        }

        const style = window.getComputedStyle(element);
        const isVisible =
          style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
        return isVisible;
      });

      // Only use available steps - don't show tour if no elements exist
      const stepsToUse = availableSteps.length > 0 ? availableSteps : [];

      // If we found some steps but fewer than total, keep retrying for conditional tabs
      const shouldRetry =
        stepsToUse.length > 0 &&
        stepsToUse.length < allSteps.length &&
        retryCount < maxRetries;

      if (stepsToUse.length > 0 && !shouldRetry) {
        // Transform steps to match the expected format with icon as string
        const transformedSteps: TransformedTourStep[] = stepsToUse.map((step) => {
          // Add '#' prefix if not present (same logic as isElementVisible)
          const elementSelector =
            step.element.startsWith('#') || step.element.startsWith('.')
              ? step.element
              : `#${step.element}`;

          return {
            element: elementSelector,
            description: step.description,
            icon: step.icon?.publicLink, // Extract publicLink from icon object
            position: step.position,
            title: step.title,
            tabId: step.tabId, // Use the tabId we determined earlier
          };
        });
        setSteps(transformedSteps);
        setShowTour(true);
      } else if (shouldRetry || (stepsToUse.length === 0 && retryCount < maxRetries)) {
        retryCount++;
        setTimeout(checkElements, 100);
        if (retryCount === 1 && stepsToUse.length === 0) {
          setShowTour(false);
        }
      } else if (stepsToUse.length > 0) {
        // We've hit max retries but have some steps, use what we found
        const transformedSteps: TransformedTourStep[] = stepsToUse.map((step) => {
          const elementSelector =
            step.element.startsWith('#') || step.element.startsWith('.')
              ? step.element
              : `#${step.element}`;

          return {
            element: elementSelector,
            description: step.description,
            icon: step.icon?.publicLink,
            position: step.position,
            title: step.title,
            tabId: step.tabId,
          };
        });
        setSteps(transformedSteps);
        setShowTour(true);
      } else {
        // No steps found after all retries - clear everything
        setSteps([]);
        setShowTour(false);
      }
    };

    // Initial check with a delay to allow DOM and async data to load
    const timer = setTimeout(checkElements, 800);
    return () => clearTimeout(timer);
  }, [pathname, tourKey, allSteps.length]);

  // Only call useTour when we have steps and are ready
  const tourCondition = steps.length > 0 && !tourCompleted && !hasSeenTour;

  useTour({
    id: tourData?.id,
    steps: tourCondition ? steps : [],
    condition: tourCondition && !tourCompleted,
    delay: tourDelay, // Use delay from backend or default
    onComplete: () => {
      if (tourData?.id) {
        addSeenTour(String(tourData.id));
      }
      setShowTour(false);
      setTourCompleted(true);
      // Invalidate tour query to refetch from backend
      queryClient.invalidateQueries({ queryKey: [GET_TOUR, param, previewId] });
    },
    onDestroy: () => {
      if (tourData?.id) {
        addSeenTour(String(tourData.id));
      }
      // Called when tour is destroyed (completed or closed)
      setShowTour(false);
      setTourCompleted(true);
      // Invalidate tour query to refetch from backend
      queryClient.invalidateQueries({ queryKey: [GET_TOUR, param, previewId] });
    },
  });

  return null;
}

export default function AutoTour() {
  return (
    <Suspense fallback={null}>
      <AutoTourContent />
    </Suspense>
  );
}
